import { useEffect, useMemo, useRef, useState } from "react";
import FocusLock from "react-focus-lock";
import { Animated, Pressable } from "react-native";
import { Portal } from "react-portal";

import { useTheme } from "@smartrent/shared-ui";

import { createAnimationFromObject } from "./utils";

import type { ModalWebProps } from "@smartrent/shared-ui";

export interface ModalProps extends ModalWebProps {}

export function Modal({
  visible,
  children,
  onBackdropPress,
  backdropOpacity = 0.5,
  animation = {
    from: {
      opacity: 0,
      transform: [{ translateY: 50 }],
    },
    to: {
      opacity: 1,
      transform: [{ translateY: 0 }],
    },
  },
  animationTiming = 300,
  testID,
  style,
}: ModalProps) {
  const { colors } = useTheme();
  const backdropAnim = useRef(new Animated.Value(0)).current;
  const contentAnim = useRef(new Animated.Value(0)).current;
  const [displayContent, setDisplayContent] = useState(false);

  const animationStyles = useMemo(() => {
    return createAnimationFromObject(animation, contentAnim, [0, 1]);
  }, [animation, contentAnim]);

  useEffect(() => {
    if (visible) {
      setDisplayContent(true);

      Animated.parallel([
        Animated.spring(backdropAnim, {
          toValue: 1,
          overshootClamping: true,
          useNativeDriver: false,
          delay: 250,
        }),
        Animated.timing(contentAnim, {
          toValue: 1,
          duration: animationTiming,
          useNativeDriver: false,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.spring(backdropAnim, {
          toValue: 0,
          overshootClamping: true,
          useNativeDriver: false,
          delay: 250,
        }),
        Animated.timing(contentAnim, {
          toValue: 0,
          duration: animationTiming,
          useNativeDriver: false,
        }),
      ]).start(() => {
        setDisplayContent(false);
      });
    }
  }, [animationTiming, backdropAnim, contentAnim, visible]);

  return displayContent ? (
    <Portal>
      <FocusLock>
        <Animated.View
          testID={testID}
          style={[
            {
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
            },
            style,
          ]}
        >
          <Pressable
            onPress={onBackdropPress}
            testID="backdrop"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
            }}
          >
            <Animated.View
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: colors.black,
                opacity: backdropAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, backdropOpacity],
                }),
              }}
            />
          </Pressable>
          <Animated.View
            style={[
              {
                alignItems: "center",
                justifyContent: "center",

                height: "auto",
                width: "auto",
              },
              animationStyles,
            ]}
          >
            {children}
          </Animated.View>
        </Animated.View>
      </FocusLock>
    </Portal>
  ) : null;
}
